<template>
  <ProductSpecs
    :groups="productSpecsGroups"
    :lightbox-gallery="lightboxGallery"
  />
  <div
    v-if="store.decoratedProduct?.productDocumentationLink"
    class="product-specs"
  >
    <div class="product-specs__group">
      <header class="product-specs__group-header">
        <h4
          :class="['product-specs__group-headline', $style.prodSpecGroupHead]"
        >
          {{
            $textDictionary['pdp.specifications.headline.label'] ??
            'documentation_available'
          }}
        </h4>
      </header>
      <div
        :class="['product-specs__group-content', $style.prodSpecGroupoContent]"
      >
        <HyperLink v-bind="documentationLink">
          {{
            $textDictionary['pdp.specifications.link.label'] ??
            'documentation_url'
          }}
        </HyperLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductSpecs, HyperLink } from '@hypercodestudio/basler-components';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';

const store = useProductDetailPageStore();
const { $textDictionary } = useNuxtApp();

const productSpecsGroups = computed(
  () => store.decoratedProduct?.getProductSpecifications(frcLink.value) ?? []
);

const documentationLink = computed(
  (): HyperlinkInterface => ({
    uri: store.decoratedProduct?.productDocumentationLink ?? '',
    external: true,
    target: '_blank'
  })
);

const frcCameraName = computed(() => {
  if (store.decoratedProduct?.productDocumentationLinkSlug) {
    const slug = store.decoratedProduct?.productDocumentationLinkSlug;
    const re = new RegExp(slug, 'gi');
    const product = store.decoratedProduct?.name ?? '';
    return product.match(re) ?? '';
  }
  return store.decoratedProduct?.name ?? '';
});

const { data: frcResponse } = store.decoratedProduct?.name
  ? await useFetch<{ isInFrc: boolean }>('/api/tools/isInFrc', {
      watch: [() => store.decoratedProduct?.name],
      query: {
        name: frcCameraName
      }
    })
  : {};

const showFrcLink = computed(() => {
  return frcResponse ? frcResponse.value?.isInFrc : false;
});

const frcLink = computed(() => {
  if (showFrcLink.value) {
    return (
      '<a id="pdp-frc-spec-button" style="margin-left:10px;" target="_blank" href="' +
      $textDictionary.value['pdp.frc.linkhref'] +
      '#camera=' +
      store.decoratedProduct?.name +
      '" title="' +
      $textDictionary.value['pdp.frc.link.tooltip'] +
      '">' +
      ($textDictionary.value['pdp.frc.linktext'] ?? 'Calculate Framerate') +
      '<span class="icon icon--arrow-right-circle icon--dynamic" style="top: 2px;position: relative;right: -4px;" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" data-name="Website Icons Button" viewBox="0 0 100 100"><path d="M55.33 29.04a3.126 3.126 0 0 0-4.42 4.42l13.42 13.41H28.12a3.124 3.124 0 0 0 0 6.25h36.21L50.91 66.54a3.126 3.126 0 0 0 4.42 4.42l18.75-18.75c.6-.61.91-1.41.91-2.21s-.31-1.6-.92-2.21L55.32 29.04ZM50 0C22.38 0 0 22.38 0 50s22.38 50 50 50 50-22.38 50-50S77.62 0 50 0Zm0 93.75C25.88 93.75 6.25 74.12 6.25 50S25.88 6.25 50 6.25 93.75 25.88 93.75 50 74.12 93.75 50 93.75Z" style="stroke-width:0"></path></svg></span></a>'
    );
  }
});
const lightboxGallery = computed(() => {
  if (!store.decoratedProduct) {
    return [];
  }

  return {
    title:
      $textDictionary.value['media.gallery.title'] ??
      'Gallery of all media elements',
    isOpen: false,
    triggerBtnLabel:
      $textDictionary.value['media.gallery.trigger.btn.label'] ??
      'Open media in lightbox',
    nextBtnLabel:
      $textDictionary.value['media.gallery.next.btn.label'] ?? 'Next media',
    prevBtnLabel:
      $textDictionary.value['media.gallery.prev.btn.label'] ?? 'Previous media',
    closeBtnLabel:
      $textDictionary.value['media.gallery.close.btn.label'] ??
      'Close lightbox',
    mediaList:
      store.decoratedProduct?.getProductSpecificationsLightboxGallery() ?? []
  };
});
</script>

<style module>
.prodSpecGroupHead {
  margin: 0;
}

.prodSpecGroupoContent {
  line-height: 2.25;
  font-size: 1rem;
}
@media (max-width: 768px) {
  .product-specs__group {
    margin-block-end: var(--spacing-size-small);
  }
}
</style>
